import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ComingSoonBadge } from "@/components/ComingSoonBadge";

const FREE_THRESHOLD = 75;

export function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_THRESHOLD - subtotal);
  const pct = Math.min(100, (subtotal / FREE_THRESHOLD) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col border-l border-white/10 bg-[#0B0D16] text-slate-100 sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2 text-xl text-slate-100">
            <ShoppingBag className="h-5 w-5 text-[#00F0FF]" /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <ShoppingBag className="h-14 w-14 text-slate-700" />
            <p className="text-slate-400">Your cart is empty.</p>
            <Button onClick={() => { setOpen(false); navigate("/shop"); }} className="rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="cart-empty-shop-btn">
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-2 rounded-xl border border-white/10 bg-[#111420] p-3">
              {remaining > 0 ? (
                <p className="text-xs text-slate-300">Add <span className="font-mono text-[#00F0FF]">{format(remaining)}</span> more for <span className="font-semibold text-white">FREE global shipping</span></p>
              ) : (
                <p className="text-xs font-semibold text-[#00E676]">🎉 You've unlocked FREE global shipping!</p>
              )}
              <Progress value={pct} className="mt-2 h-1.5 bg-white/10" />
            </div>

            <div className="-mx-6 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3" data-testid={`cart-item-${i.product_id}`}>
                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-[#0B0D16]">
                      <img src={i.image} alt={i.name} className="h-full w-full object-cover opacity-90" />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex justify-between gap-2">
                        <p className="text-sm font-medium leading-snug line-clamp-2">{i.name}</p>
                        <button onClick={() => removeItem(i.product_id)} className="text-slate-500 hover:text-[#FF2A6D]" data-testid={`cart-remove-${i.product_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {i.coming_soon && <ComingSoonBadge className="mt-1 scale-90 origin-left" />}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center rounded-lg border border-white/10">
                          <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-1.5 hover:text-[#00F0FF]" data-testid={`cart-dec-${i.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                          <span className="w-7 text-center font-mono text-sm">{i.quantity}</span>
                          <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-1.5 hover:text-[#00F0FF]" data-testid={`cart-inc-${i.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <span className="font-mono text-sm font-bold">{format(i.price_usd * i.quantity)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-slate-400">Subtotal</span>
                <span className="font-mono text-xl font-bold text-white" data-testid="cart-subtotal">{format(subtotal)}</span>
              </div>
              <Button onClick={() => { setOpen(false); navigate("/checkout"); }} className="w-full rounded-xl bg-[#00F0FF] py-6 text-base font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="cart-checkout-btn">
                Secure Checkout <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
